<template>
	<section>
		<AdaptiveContainer class="flex justify-between items-center">
			<BaseHeading
				type="h5"
				class="my-9 col-span-full relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
			>
				<template v-if="!loadingLatest">
					{{ $t("newestJobs") }}
				</template>
				<template v-else>
					<div class="w-[240px] h-6 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal animate-pulse"></div>
				</template>
			</BaseHeading>
			<div class="text-sm hidden sm:flex">
				<NuxtLink :to="`/${$i18n.locale}/jobs?new_job=true`" type="grey" class="flex items-center text-sm">
					{{ $t("allJobs") }}
					<span class="material-icons-outlined pl-2 text-base">arrow_forward</span>
				</NuxtLink>
			</div>
		</AdaptiveContainer>
		<CarouselJobs
			:data-url="latestJobsUrl"
			:slideWidth="320"
			@loaded="loadingLatest = false"
			bannerType="newest-jobs"
		/>

		<!-- Loading State -->
		<AdaptiveContainer v-if="loadingLatest">
			<div class="block w-[1000vw] mt-6 mb-12">
				<div v-for="(item, index) in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
					<DjCardJob :loading="true" />
				</div>
			</div>
		</AdaptiveContainer>

		<div class="sm:hidden mb-6">
			<NuxtLink
				:to="`/${$i18n.locale}/jobs?new_job=true`"
				class="w-full flex justify-center items-center bg-dj-blue/5 text-sm text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10 max-w-[320px] mx-auto mb-4 p-3 rounded-normal"
			>
				{{ $t("allJobs") }}
				<span class="material-icons-outlined pl-2">arrow_forward</span>
			</NuxtLink>
		</div>

		<div class="bg-gradient-to-b from-[#F3F7FD]">
			<template v-if="isAuth">
				<JobsPageCarousel
					:title="$t('jobs.recommended')"
					:carousel-data-url="recommendedCarouselUrl"
					:data-url="recommendedDataUrl"
					hasGradientBg
					route-param="allRecommendedJobs"
					@data-fetched="processApiData"
					sortByGold
					carouselType="offered-jobs"
				/>
			</template>
		</div>
	</section>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";

export default defineComponent({
	name: "SectionsJobsAsUser",
	setup() {
		const { locale } = useI18n();

		const isAuth = useState("isAuth");

		const loadingLatest = ref(true);
		const latestJobsUrl = "jobs?scope[]=latestJobs";

		const recommendedCarouselUrl = ref("jobs?");
		const recommendedDataUrl = ref(globalConfig.API);

		onMounted(() => {
			if (isAuth.value === true) {
				buildRecommendedUrl();
			}
		});

		function buildRecommendedUrl() {
			//  Always add the region and the jobsForYou scope first!
			recommendedCarouselUrl.value += `region=${useRegion.getCurrentRegion().code.toLowerCase()}`;
			recommendedCarouselUrl.value += "&scope[]=jobsForYou";

			// Add global config scopes
			if (globalConfig.isBlue) {
				recommendedCarouselUrl.value += "&scope[]=isBlue";
			}
			if (globalConfig.isNotBlueHU) {
				recommendedCarouselUrl.value += "&scope[]=isNotBlue";
			}

			// Check for user tags
			const user = useAuth.getUser();
			const userTags = user?.tags;

			if (userTags) {
				// categories
				if (userTags.job_category) {
					userTags.job_category.data?.forEach((cat) => {
						recommendedCarouselUrl.value += `&tags[job-category][]=${cat.id}`;
					});
				}
				// subcategories
				if (userTags.job_subcategory) {
					userTags.job_subcategory.data?.forEach((cat) => {
						recommendedCarouselUrl.value += `&tags[job-subcategory][]=${cat.id}`;
					});
				}
				// job levels
				if (userTags.job_level) {
					userTags.job_level.data?.forEach((level) => {
						recommendedCarouselUrl.value += `&tags[job-level][]=${level.id}`;
					});
				}
			}

			// Finalize the data url
			recommendedDataUrl.value += `/${recommendedCarouselUrl.value}`;
		}

		function processApiData(event: { param: string }) {
			const router = useRouter();
			const query: Record<string, string> = {};
			query[event.param] = "true";
			router.push({
				path: `/${locale.value}/jobs`,
				query: query,
			});
		}

		return {
			isAuth,
			loadingLatest,
			latestJobsUrl,
			recommendedCarouselUrl,
			recommendedDataUrl,
			processApiData,
		};
	},
});
</script>
